import { useRef, useLayoutEffect, useState, useEffect } from "react";
import { splitTextByColorRules } from "../../utils/colorRules";

export default function AutoResizeTextarea({
  data,
  onChange,
  onBlur,
  placeholder = "",
  className = "",
  style = {},
  rows = 4,

  // 🧹 Custom props (filtered out so they don't reach the DOM)
  osm,
  reportName,
  onRowRemoved,
  activeSection,
  availablePTSSections,
  setSections,
  propertyId,
  showPart,
  rakennusData,
  reportId,
  onUserLinked,
  onRowSaved,
  subsectionTitle,

  ...rest // 👈 Only pass safe DOM props to textarea
}) {
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const value = data?.value || "";

  const [history, setHistory] = useState([value]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // --- Auto-resize logic ---
  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
    if (overlayRef.current) {
      overlayRef.current.scrollTop = textarea.scrollTop;
    }
  };

  useLayoutEffect(() => {
    const handle = requestAnimationFrame(resizeTextarea);
    window.addEventListener("resize", resizeTextarea);
    return () => {
      cancelAnimationFrame(handle);
      window.removeEventListener("resize", resizeTextarea);
    };
  }, [value]);

  // --- Undo/Redo handlers ---
  const handleChange = (e) => {
    const newValue = e.target.value;
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newValue);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    onChange({ ...data, value: newValue });
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      onChange({ ...data, value: history[newIndex] });
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      onChange({ ...data, value: history[newIndex] });
    }
  };

  const parts = splitTextByColorRules(value);

  // --- Keep overlay scroll synced with textarea ---
  const syncScroll = (e) => {
    if (overlayRef.current) {
      overlayRef.current.scrollTop = e.target.scrollTop;
      overlayRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // --- Sync when external data changes (e.g. undo/redo) ---
  useEffect(() => {
    resizeTextarea();
  }, [value]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* Undo/Redo buttons */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "12px",
          display: "flex",
          gap: "6px",
          zIndex: 3,
        }}
      >
        <button
          type="button"
          onClick={undo}
          disabled={historyIndex === 0}
          style={buttonStyle(historyIndex === 0)}
          title="Undo"
        >
          ↶
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={historyIndex === history.length - 1}
          style={buttonStyle(historyIndex === history.length - 1)}
          title="Redo"
        >
          ↷
        </button>
      </div>

      {/* Highlight overlay */}
      <div
        ref={overlayRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          padding: "2.5rem 3.5rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          pointerEvents: "none",
          overflow: "hidden",
          fontSize: "15px",
          fontFamily: "Segoe UI, Roboto, Arial, sans-serif",
          lineHeight: "1.7",
          color: "#000",
          zIndex: 1,
        }}
      >
        {parts.map((p, i) => (
          <span
            key={i}
            style={{
              color: p.color || "#000",
              fontWeight: p.bold ? "bold" : "normal",
            }}
          >
            {p.text}
          </span>
        ))}
      </div>

      {/* Actual textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={rows}
        className={className}
        spellCheck={false}
        lang="fi"
        wrap="soft"
        onScroll={syncScroll}
        style={{
          width: "100%",
          minHeight: "250px",
          fontSize: "15px",
          fontFamily: "Segoe UI, Roboto, Arial, sans-serif",
          lineHeight: "1.7",
          padding: "2.5rem 3.5rem",
          border: "1px solid #bbb",
          borderRadius: "10px",
          boxShadow: "inset 0 2px 5px rgba(0,0,0,0.05)",
          resize: "none",
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          backgroundColor: "transparent",
          color: "transparent",
          caretColor: "#000",
          position: "relative",
          zIndex: 2,
          ...style,
        }}
        {...rest} // only safe props
      />
    </div>
  );
}

function buttonStyle(disabled) {
  return {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "1px solid #ccc",
    background: "#f8f9fa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "15px",
    opacity: disabled ? 0.4 : 1,
  };
}